/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useEffect, useState } from 'react';

import { AbrainFavicon } from '@coze-studio/abr-brand';
import { Button, Form } from '@coze-arch/coze-design';

const REMEMBER_ACCOUNT_KEY = 'cozeLoginAccount';

function readRememberedAccount(): string {
  try {
    return localStorage.getItem(REMEMBER_ACCOUNT_KEY)?.trim() || '';
  } catch (error) {
    void error;
    return '';
  }
}

function persistRememberedAccount(account: string, remember: boolean): void {
  try {
    if (remember) {
      localStorage.setItem(REMEMBER_ACCOUNT_KEY, account.trim());
      return;
    }
    localStorage.removeItem(REMEMBER_ACCOUNT_KEY);
  } catch (error) {
    void error;
  }
}

interface UcLoginPanelProps {
  account: string;
  password: string;
  hasError: boolean;
  loginLoading: boolean;
  onAccountChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onErrorChange: (hasError: boolean) => void;
  onLogin: () => void;
}

export const UcLoginPanel: FC<UcLoginPanelProps> = ({
  account,
  password,
  hasError,
  loginLoading,
  onAccountChange,
  onPasswordChange,
  onErrorChange,
  onLogin,
}) => {
  const [rememberAccount, setRememberAccount] = useState(() =>
    Boolean(readRememberedAccount()),
  );

  useEffect(() => {
    const saved = readRememberedAccount();
    if (saved) {
      onAccountChange(saved);
    }
    // 仅挂载时回填记住的账号
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only restore
  }, []);

  const submitDisabled = !account || !password || hasError;

  const handleLogin = () => {
    persistRememberedAccount(account, rememberAccount);
    onLogin();
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_480px] bg-[#f8fafc]">
      <section className="relative hidden lg:flex items-center overflow-hidden bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] px-12 py-16 text-[#f8fafc]">
        <div className="relative z-[1] max-w-[520px]">
          <p className="m-0 mb-3 text-[13px] font-semibold tracking-[0.08em] uppercase text-[#94a3b8]">
            企业级本体与数据服务
          </p>
          <h1 className="m-0 mb-4 text-[clamp(28px,3.2vw,40px)] font-bold leading-[1.15]">
            爱波瑞 Agent
          </h1>
          <p className="m-0 text-base leading-[1.65] text-[#cbd5e1]">
            安全、可信的 AI Agent 开发平台。请使用企业账号登录以继续。
          </p>
        </div>
        <div
          className="pointer-events-none absolute -right-[20%] -bottom-[30%] h-[70%] w-[70%] rounded-full"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, rgba(3, 105, 161, 0.35), transparent 55%)',
          }}
        />
      </section>

      <section className="flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-14">
        <div className="mx-auto w-full max-w-[400px] rounded-2xl border border-[#e2e8f0] bg-white p-8 shadow-[0_12px_40px_rgba(15,23,42,0.08)]">
          <div className="mb-6 flex justify-center">
            <AbrainFavicon />
          </div>
          <h2 className="m-0 mb-2 text-center text-[22px] font-bold text-[#0f172a]">
            登录
          </h2>
          <p className="m-0 mb-6 text-center text-sm text-[#475569]">
            使用账号与密码进入系统
          </p>

          <Form
            onErrorChange={errors => {
              onErrorChange(Object.keys(errors).length > 0);
            }}
          >
            <label
              className="mb-1 block text-[13px] font-semibold text-[#334155]"
              htmlFor="uc-login-account"
            >
              账号
            </label>
            <Form.Input
              id="uc-login-account"
              data-testid="login.input.account"
              noLabel
              type="text"
              field="account"
              rules={[{ required: true, message: '请输入账号' }]}
              onChange={onAccountChange}
              placeholder="请输入账号/手机号"
            />
            <label
              className="mb-1 block text-[13px] font-semibold text-[#334155]"
              htmlFor="uc-login-password"
            >
              密码
            </label>
            <Form.Input
              id="uc-login-password"
              data-testid="login.input.password"
              noLabel
              field="password"
              type="password"
              rules={[{ required: true, message: '请输入密码' }]}
              onChange={onPasswordChange}
              placeholder="请输入密码"
            />
          </Form>

          <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-[#475569]">
            <input
              type="checkbox"
              checked={rememberAccount}
              onChange={e => setRememberAccount(e.target.checked)}
            />
            记住账号
          </label>

          <Button
            data-testid="login.button.login"
            className="mt-4 w-full !rounded-[10px] !py-3 !font-semibold"
            disabled={submitDisabled}
            onClick={handleLogin}
            loading={loginLoading}
            color="hgltplus"
          >
            登录
          </Button>
        </div>
        <p className="mx-auto mt-5 max-w-[400px] text-center text-xs leading-[1.5] text-[#64748b]">
          如需开通账号或重置密码，请联系企业管理员
        </p>
      </section>
    </div>
  );
};
